/**
 * REST API Client - Replaces Apollo Client/GraphQL
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Helper to get auth token
const getAuthToken = () => {
  const user = localStorage.getItem('BEGROSS_USER');
  if (user) {
    try {
      const userData = JSON.parse(user);
      return userData.AUTHTOKEN;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Helper to get session ID
const getSessionId = () => {
  return localStorage.getItem('woo-session') || null;
};

// Base fetch function
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const sessionId = getSessionId();
  if (sessionId) {
    headers['woocommerce-session'] = sessionId;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  console.log('API Request:', { method: options.method || 'GET', url, body: options.body });

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    console.log('API Response:', {
      status: response.status,
      statusText: response.statusText,
      url,
      headers: Object.fromEntries(response.headers.entries())
    });

    // Handle session ID from response
    const sessionHeader = response.headers.get('woocommerce-session') || response.headers.get('set-cookie');
    if (sessionHeader && sessionHeader !== getSessionId()) {
      // Extract session ID from header (might be "Session <id>" format)
      const extractedSessionId = sessionHeader.replace(/^Session\s+/i, '').trim();
      if (extractedSessionId) {
        localStorage.setItem('woo-session', extractedSessionId);
      }
    }

    if (!response.ok) {
      let errorData;
      try {
        const text = await response.text();
        console.error('Error response text:', text);
        errorData = text ? JSON.parse(text) : { error: `HTTP ${response.status}: ${response.statusText}` };
      } catch (e) {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      const errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
      console.error('API Error Details:', {
        status: response.status,
        statusText: response.statusText,
        url,
        error: errorData,
        requestBody: options.body
      });
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('API Success:', { url, data });
    return data;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network Error - Is the backend server running?', {
        url,
        error: error.message
      });
      throw new Error(`Cannot connect to server. Please check if the backend is running at ${API_BASE_URL}`);
    }
    console.error('API Request Error:', {
      url,
      error: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// API Methods
const api = {
  // Products
  getProducts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/products?${queryString}`);
  },

  getProduct: (id) => {
    return apiRequest(`/products/${id}`);
  },

  // Categories
  getCategories: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/categories?${queryString}`);
  },

  // Cart
  getCart: () => {
    return apiRequest('/cart');
  },

  addToCart: (productId, quantity = 1) => {
    return apiRequest('/cart/add', {
      method: 'POST',
      body: JSON.stringify({ productId, quantity }),
    });
  },

  updateCartItem: (key, quantity) => {
    return apiRequest('/cart/update', {
      method: 'PUT',
      body: JSON.stringify({ key, quantity }),
    });
  },

  removeFromCart: (key) => {
    return apiRequest('/cart/remove', {
      method: 'DELETE',
      body: JSON.stringify({ key }),
    });
  },

  emptyCart: () => {
    return apiRequest('/cart/empty', {
      method: 'POST',
    });
  },

  // Auth
  login: (username, password) => {
    return apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  register: (userData) => {
    return apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  getCurrentUser: () => {
    return apiRequest('/auth/me');
  },

  // Theme
  getThemeOptions: () => {
    return apiRequest('/theme/options');
  },

  // Checkout
  checkout: (checkoutData) => {
    return apiRequest('/checkout', {
      method: 'POST',
      body: JSON.stringify(checkoutData),
    });
  },

  // Customer
  getCustomerAddresses: () => {
    return apiRequest('/customer/addresses');
  },

  // Orders
  getOrders: () => {
    return apiRequest('/orders');
  },

  getOrder: (id) => {
    return apiRequest(`/orders/${id}`);
  },

  // Admin
  getAdminProducts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/products?${queryString}`);
  },

  createProduct: (productData) => {
    return apiRequest('/admin/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  },

  updateProduct: (id, productData) => {
    return apiRequest(`/admin/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  },

  deleteProduct: (id) => {
    return apiRequest(`/admin/products/${id}`, {
      method: 'DELETE',
    });
  },

  getAdminOrders: () => {
    return apiRequest('/admin/orders');
  },

  getAdminCategories: () => {
    return apiRequest('/admin/categories');
  },

  createCategory: (categoryData) => {
    return apiRequest('/admin/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  },

  updateCategory: (id, categoryData) => {
    return apiRequest(`/admin/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  },

  deleteCategory: (id) => {
    return apiRequest(`/admin/categories/${id}`, {
      method: 'DELETE',
    });
  },

  getAdminCustomers: () => {
    return apiRequest('/admin/customers');
  },

  // Brands
  getAdminBrands: () => {
    return apiRequest('/admin/brands');
  },

  createBrand: (brandData) => {
    return apiRequest('/admin/brands', {
      method: 'POST',
      body: JSON.stringify(brandData),
    });
  },

  updateBrand: (id, brandData) => {
    return apiRequest(`/admin/brands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(brandData),
    });
  },

  deleteBrand: (id) => {
    return apiRequest(`/admin/brands/${id}`, {
      method: 'DELETE',
    });
  },

  // Theme Options
  getAdminThemeOptions: () => {
    return apiRequest('/admin/theme-options');
  },

  updateThemeOptions: (themeOptions) => {
    return apiRequest('/admin/theme-options', {
      method: 'PUT',
      body: JSON.stringify({ themeOptions }),
    });
  },

  // Upload
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = getAuthToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const sessionId = getSessionId();
    if (sessionId) {
      headers['woocommerce-session'] = sessionId;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  },
};

export default api;

